/**
 * wine-drawer.css — Tasting notes / food pairings drawer (all wineries).
 *
 * Themed via --ws-drawer-* (defaults in variables.css; per-winery overrides
 * in variant styles.css). Mirrors old RedConcierge _tasting_notes.scss.
 */

@keyframes ws-drawer-spin {
  to { transform: rotate(360deg); }
}

.ws-panel {
  position: relative;
}

.ws-wine-drawer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: var(--ws-drawer-bottom, 90px);
  z-index: 10050;
  font-family: var(--ws-font);
  background: rgba(0, 0, 0, 0.45);
  border-radius: 10px;
  visibility: hidden;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}

.ws-wine-drawer.is-open {
  visibility: visible;
  opacity: 1;
  pointer-events: auto;
}

.ws-wine-drawer-sheet {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: calc(100% - 70px);
  z-index: 1;
  transform: translateY(100%);
  transition: transform 420ms cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform;
}

.ws-wine-drawer.is-open .ws-wine-drawer-sheet {
  transform: translateY(0);
}

.ws-wine-drawer-close-fab {
  position: absolute;
  top: -64px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.18);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: var(--ws-drawer-heading, var(--ws-rich-deep));
}

.ws-wine-drawer-panel {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  background: #fff;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.12);
}

.ws-wine-drawer-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px 16px 12px;
  color: var(--ws-drawer-heading, var(--ws-rich-deep));
  border-bottom: 1px solid #f1f1f1;
  flex-shrink: 0;
}

.ws-wine-drawer-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--ws-drawer-heading, var(--ws-rich-deep));
  line-height: 1.2;
}

.ws-wine-drawer-header-icon svg {
  display: block;
  color: var(--ws-drawer-heading, var(--ws-rich-deep));
}

.ws-wine-drawer-product {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f1f1;
  flex-shrink: 0;
}

.ws-wine-drawer-image-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 72px;
  border: 1px solid #f5eae9;
  border-radius: 6px;
  background: #fff;
  padding: 4px;
  flex-shrink: 0;
}

.ws-wine-drawer-image-wrap img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.ws-wine-drawer-product-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  display: none;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background-color: var(--ws-drawer-heading, var(--ws-rich-deep));
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  border-radius: 50%;
  line-height: 1;
  z-index: 2;
}

.ws-wine-drawer-product-info {
  min-width: 0;
}

.ws-wine-drawer-product-title {
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1d;
  line-height: 1.3;
}

.ws-wine-drawer-pricing {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.ws-wine-drawer-compare-price {
  font-size: 12px;
  color: var(--ws-drawer-muted, #737373);
  text-decoration: line-through;
}

.ws-wine-drawer-regular-price {
  font-size: 16px;
  font-weight: 700;
  color: var(--ws-drawer-heading, var(--ws-rich-deep));
}

.ws-wine-drawer-body {
  display: flex;
  min-height: 0;
  flex: 1;
  overflow: hidden;
}

.ws-wine-drawer-tabs {
  display: flex;
  flex-direction: column;
  width: 76px;
  flex-shrink: 0;
  border-right: 1px solid #f1f1f1;
  background: #fafafa;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.ws-wine-drawer-tabs.collapsed {
  width: 44px;
}

.ws-wine-drawer-tabs.collapsed .ws-wine-drawer-tab:not(.ws-drawer-tab-collapse) {
  min-height: 56px;
  padding: 10px 4px;
  gap: 0;
}

.ws-wine-drawer-tabs.collapsed .ws-wine-drawer-tab:not(.ws-drawer-tab-collapse) span:not(.ws-drawer-tab-icon) {
  display: none;
}

.ws-wine-drawer-tabs.collapsed .ws-drawer-tab-collapse svg {
  transform: scaleX(-1);
}

.ws-drawer-tab-collapse {
  appearance: none;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 12px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  border-bottom: 1px solid #f1f1f1;
}

.ws-drawer-tab-collapse svg {
  display: block;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.ws-drawer-tab-collapse svg path {
  stroke: var(--ws-drawer-heading, var(--ws-rich-deep));
}

.ws-wine-drawer-tab {
  appearance: none;
  border: none;
  background: transparent;
  color: #1d1d1d;
  cursor: pointer;
  padding: 14px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  min-height: 88px;
  border-bottom: 1px solid #f1f1f1;
  transition: padding 0.3s, min-height 0.3s, gap 0.3s;
}

.ws-wine-drawer-tab.active {
  background: var(--ws-drawer-tab-active, var(--ws-rich-accent));
  color: var(--ws-drawer-tab-active-text, var(--ws-rich-on-accent));
  border-right: 5px solid var(--ws-drawer-heading, var(--ws-rich-deep));
}

.ws-drawer-tab-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.ws-wine-drawer-content {
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background: #fff;
}

.ws-wine-drawer-pane {
  display: none;
  height: 100%;
  overflow-y: auto;
  padding: 14px 16px 16px;
  scrollbar-width: thin;
  color: #1d1d1d;
}

.ws-wine-drawer-pane.active {
  display: block;
}

.ws-wine-drawer-intro {
  background: var(--ws-drawer-surface, var(--ws-rich-inner-from, #fdf5f7));
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-line;
  color: #1d1d1d;
}

.ws-wine-drawer-pane h4 {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--ws-drawer-heading, var(--ws-rich-deep));
}

.ws-wine-drawer-pane p {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.45;
  color: #1d1d1d;
}

.ws-drawer-fp-blurb {
  font-size: 13px;
  line-height: 1.35;
  margin: 0 0 12px;
}

.ws-drawer-fp-heading {
  font-size: 16px;
  font-weight: 700;
  color: var(--ws-drawer-heading, var(--ws-rich-deep));
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.ws-drawer-recipe-refresh {
  border: none;
  font-size: 14px;
  color: #1d1d1d;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.ws-drawer-fp-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.ws-drawer-fp-card {
  border-top: 3px solid var(--ws-drawer-card-accent, var(--ws-rich-deep));
  background: var(--ws-drawer-surface, var(--ws-rich-inner-from, #fdf5f7));
  border-radius: 10px;
  padding: 8px;
  cursor: pointer;
}

.ws-drawer-fp-card-img {
  width: 100%;
  height: 72px;
  object-fit: cover;
  border-radius: 6px;
  margin-bottom: 6px;
  display: block;
}

.ws-drawer-fp-card h4 {
  color: var(--ws-drawer-heading, var(--ws-rich-deep));
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 6px;
}

.ws-drawer-fp-card p {
  font-size: 12px;
  line-height: 1.4;
  margin: 0;
}

.ws-drawer-fp-previous-title {
  font-size: 12px;
  color: var(--ws-drawer-heading, var(--ws-rich-deep));
  margin: 16px 0 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ws-drawer-loading {
  text-align: center;
  padding: 24px;
}

.ws-drawer-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--ws-bot-bubble-bg);
  border-top-color: #999898;
  border-radius: 50%;
  animation: ws-drawer-spin 0.8s linear infinite;
  margin: 0 auto 12px;
}

.ws-drawer-loading-text {
  color: #888;
  font-size: 13px;
  margin: 0;
}

.ws-drawer-error-text {
  color: #888;
  text-align: center;
  padding: 24px;
  margin: 0;
}

.ws-drawer-retry-btn {
  margin-top: 8px;
  padding: 6px 16px;
  border: 1px solid var(--ws-drawer-heading, var(--ws-rich-deep));
  border-radius: 6px;
  background: none;
  color: var(--ws-drawer-heading, var(--ws-rich-deep));
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}

.ws-drawer-recipe-detail {
  display: none;
  background: #fff;
}

.ws-drawer-recipe-detail.is-visible {
  display: block;
}

.ws-drawer-recipe-detail [data-action="recipe-back"] {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.ws-drawer-recipe-detail [data-action="recipe-back"]:hover {
  transform: translateX(-3px);
}

.ws-drawer-recipe-header-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin: -14px -16px 4px;
  position: sticky;
  top: -14px;
  background: #fff;
  z-index: 10;
  padding: 14px 16px 6px;
  box-shadow: 0 0 0 transparent;
  transition: box-shadow 0.2s ease;
}

.ws-drawer-recipe-header-row.is-scrolled {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.ws-drawer-recipe-header-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
  cursor: pointer;
  flex: 1;
  min-width: 0;
}

.ws-drawer-recipe-back {
  font-size: 24px;
  color: var(--ws-drawer-heading, var(--ws-rich-deep));
  font-weight: 300;
  line-height: 1;
}

.ws-wine-drawer-pane .ws-drawer-recipe-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--ws-drawer-heading, var(--ws-rich-deep));
  line-height: 1.3;
}

.ws-drawer-recipe-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  align-items: center;
}

.ws-drawer-recipe-action-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ws-drawer-recipe-descriptor {
  margin: 0 0 12px;
  font-size: 12.5px;
  line-height: 1.4;
  color: #888;
}

.ws-drawer-recipe-section {
  margin: 14px 0 8px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ws-drawer-heading, var(--ws-rich-deep));
}

.ws-drawer-ingredients {
  margin: 0 0 16px;
}

.ws-drawer-ingredient-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
  line-height: 1.4;
  color: #1d1d1d;
}

.ws-drawer-ingredient-name {
  color: #1d1d1d;
}

.ws-drawer-ingredient-bullet {
  color: var(--ws-drawer-bullet, #c9a0a8);
  margin-right: 8px;
  font-size: 18px;
  line-height: 0;
  vertical-align: middle;
}

.ws-drawer-ingredient-amount {
  color: var(--ws-drawer-heading, var(--ws-rich-deep));
  font-weight: 600;
  white-space: nowrap;
  margin-left: 12px;
}

.ws-drawer-method-list {
  margin: 0 0 16px;
  padding-left: 0;
  list-style: none;
}

.ws-drawer-method-step {
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 10px;
  display: flex;
  gap: 10px;
}

.ws-drawer-method-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--ws-drawer-heading, var(--ws-rich-deep));
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1px;
}

.ws-drawer-pairing-box {
  background: var(--ws-drawer-surface, var(--ws-rich-inner-from, #fdf5f7));
  border: 1px solid color-mix(in srgb, var(--ws-drawer-heading, var(--ws-rich-deep)) 25%, #fff);
  border-radius: 10px;
  padding: 14px 16px;
  margin-top: 14px;
  margin-bottom: 16px;
}

.ws-drawer-pairing-box-title {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--ws-drawer-heading, var(--ws-rich-deep));
}

.ws-drawer-pairing-box-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
}

.ws-wine-drawer-footer {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-top: 1px solid #f1f1f1;
  background: #fff;
  flex-shrink: 0;
}

/* Old .overlay-effect on .tasting-notes-footer while Add to cart / qty runs. */
.ws-wine-drawer-footer .ws-drawer-overlay-effect {
  background-color: rgba(255, 255, 255, 0.8);
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  z-index: 999;
  display: block;
  pointer-events: auto;
}

.ws-wine-drawer-footer .ws-drawer-overlay-effect::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 40px;
  height: 40px;
  border: 4px solid #f5eded;
  border-radius: 50%;
  border-top: 4px solid var(--ws-drawer-cta, var(--ws-primary, #6e1811));
  animation: ws-drawer-overlay-spin 1s linear infinite;
}

@keyframes ws-drawer-overlay-spin {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.ws-wine-drawer-add-cart {
  width: 100%;
  /* height: 44px; */
  padding: 10px 6px;
  border-radius: 4px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  font-family: var(--ws-font);
  background-color: var(--ws-drawer-cta, var(--ws-rich-cta-bg));
  color: var(--ws-drawer-cta-text, #fff);
  border: none;
}

.ws-wine-drawer-cancel-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  color: #737373;
  margin-top: 8px;
  width: 100%;
}

.ws-wine-drawer-cancel-btn {
  background: transparent;
  color: #0a0a0a;
  border: none;
  font-weight: 600;
  font-size: 13px;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.ws-wine-drawer-qty {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-bottom: 4px;
}

.ws-wine-drawer-qty button {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 2px;
  height: 40px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.ws-wine-drawer-qty .ws-drawer-delete {
  width: 40px;
  flex: 0 0 40px;
}

.ws-wine-drawer-qty .ws-drawer-dec,
.ws-wine-drawer-qty .ws-drawer-inc {
  flex: 1 1 0%;
}

.ws-wine-drawer-qty .ws-drawer-qty-val {
  flex: 2 1 0%;
  height: 40px;
  border: 1px solid #e5e5e5;
  border-radius: 2px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 500;
  color: #0a0a0a;
}
